import { Box, Hidden, Typography, useTheme } from "@material-ui/core";
import React from "react";
import { useCounter } from "react-use";
import { encodeDelimitedArray } from "use-query-params";
import ProductCard from "../../containers/shop/ProductCard";
import {
  safeBase64Encode,
  SHOP_PRODUCTS_LIST_PARAM,
  SHOP_PRODUCTS_PATH
} from "../../helpers/url";
import useExpressDeliveryAvailability, {
  useExpressDeliveryAvailabilitySelector
} from "../../hooks/use-express-delivery-availability";
import { ProductListItemType } from "../../types/airgraft";
import NextIcon from "./NextIcon";
import PrevIcon from "./PrevIcon";
import { ArrowButton, InnerList, OutsideList, SellAllButton } from "./styles";
import { useViewportCardsCount } from "./utils";

type Props = {
  title?: string;
  seeAllLink?: string;
  products: ProductListItemType[];
};

function ProductsListSection({ title, seeAllLink, products }: Props) {
  const theme = useTheme();

  // filter avaliable products to show
  products = products.filter(p => p.disablePlp !== true);

  // Link to product list
  if (!seeAllLink) {
    const listParam = encodeDelimitedArray(
      [encodeURIComponent(title), ...products.map(p => p.originalId)],
      ","
    );
    seeAllLink = `${SHOP_PRODUCTS_PATH}?${SHOP_PRODUCTS_LIST_PARAM}=${safeBase64Encode(
      listParam
    )}`;
  }

  // filter and limit products to show
  products = products.slice(0, 8);

  const getProductExpressAvailability = useExpressDeliveryAvailabilitySelector(
    c => c.getProductExpressAvailability
  );
  const getAvailableProviderProductData = useExpressDeliveryAvailabilitySelector(
    c => c.getAvailableProviderProductData
  );

  const viewportCardsCount = useViewportCardsCount();

  const [cardIndex, { set: setCardIndex }] = useCounter(
    0,
    products.length - viewportCardsCount
  );

  const nextPage = () => {
    setCardIndex(cardIndex + viewportCardsCount);
  };

  const prevPage = () => {
    setCardIndex(Math.max(cardIndex - viewportCardsCount, 0));
  };

  return (
    <Box>
      {/* List header */}
      <Box
        mb={{ xs: 2, md: 4 }}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography variant="h5">{title}</Typography>

        <Box display="flex" alignItems="center">
          <SellAllButton to={seeAllLink}>See All</SellAllButton>

          {products.length > viewportCardsCount && (
            <Hidden xsDown implementation="css">
              <ArrowButton
                disabled={cardIndex === 0}
                style={{ marginLeft: theme.spacing(2) }}
                onClick={() => prevPage()}
              >
                <PrevIcon />
              </ArrowButton>

              <ArrowButton
                disabled={cardIndex >= products.length - viewportCardsCount}
                style={{ marginLeft: theme.spacing(0.5) }}
                onClick={() => nextPage()}
              >
                <NextIcon />
              </ArrowButton>
            </Hidden>
          )}
        </Box>
      </Box>

      {/* List products */}
      <OutsideList>
        <InnerList
          animate={{
            x: `-${(100 / products.length) * cardIndex}%`
          }}
          transition={{
            type: "spring",
            damping: 22,
            stiffness: 200,
            duration: 0.5
          }}
          className={
            products.length > viewportCardsCount
              ? "products-list--long"
              : "products-list--short"
          }
        >
          {products.map(product => {
            return (
              <ProductCard
                key={product.slug}
                variant="SHOP"
                product={product}
                shopAvailabilityLabel={getProductExpressAvailability(product, {
                  fallbackToDefault: true
                })} // Use express delivery availability (Based on current address)
                price={getAvailableProviderProductData(product)?.price?.amount}
              />
            );
          })}
        </InnerList>
      </OutsideList>
    </Box>
  );
}

export default React.memo(ProductsListSection);
